import React, { useEffect, useState } from 'react';
import { AssessmentVersion } from 'src/api/types';
import { AssessmentView } from './AssessmentView';
import { Spacer } from 'src/common';
import ComparisonView from './ComparisonView';
import { ScenariosComparison, compareScenarios } from './util';
import { encodeQueryParams } from 'src/api/client';
import TabsMenu from 'src/components/TabsMenu';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { ComparisonAssessmentQuery } from 'src/graphql/_generated';
import { formatDate } from 'src/utils/misc';

const getVersionTooltip = (version?: AssessmentVersion) => {
  if (!version) return '[CURRENT]';
  return `${version.name} | ${version.meta?.createdBy} | ${formatDate(version.meta?.createdAt)}`;
};

type AssessmentData = NonNullable<ComparisonAssessmentQuery['assessmentA']>;

interface ScenariosCompareProps {
  dataA: AssessmentData;
  dataB: AssessmentData;
  versionA?: AssessmentVersion;
  versionB?: AssessmentVersion;
  tab: 0 | 1 | 2;
  setTab: (tab: 0 | 1 | 2) => void;
}

const ScenariosCompare: React.FC<ScenariosCompareProps> = ({ dataA, dataB, versionA, versionB, tab, setTab }) => {
  const [comparison, setComparison] = useState<ScenariosComparison | null>(null);

  useEffect(() => {
    const res = compareScenarios(dataA.scenarios, dataB.scenarios, 'referenceId');
    setComparison(res);
  }, [dataA, dataB]);

  const genPath = (scenario: AssessmentData['scenarios'][0], from: 'A' | 'B') => {
    let scenA: AssessmentData['scenarios'][0] | undefined = from === 'A' ? scenario : undefined;
    let scenB: AssessmentData['scenarios'][0] | undefined = from === 'B' ? scenario : undefined;

    if (from === 'A') {
      scenB = dataB.scenarios.find((el) => el?.referenceId === scenario?.referenceId);
    } else {
      scenA = dataA.scenarios.find((el) => el?.referenceId === scenario?.referenceId);
    }

    if (!scenA && !scenB) {
      return;
    }

    if (scenA && scenB) {
      return `/compare/scenarios/${scenA.id}/${scenB.id}${encodeQueryParams({
        vaId: versionA?.id,
        vbId: versionB?.id,
      })}`;
    }

    if (scenA) {
      return `/risk-assessments/${dataA.id}/scenario/${scenA.id}${encodeQueryParams({
        version: versionA?.id,
      })}`;
    }

    if (scenB) {
      return `/risk-assessments/${dataB.id}/scenario/${scenB.id}${encodeQueryParams({
        version: versionB?.id,
      })}`;
    }

    return '';
  };

  const isSameAssessment = dataA.id === dataB.id;
  const hasVersion = Boolean(versionA || versionB);
  const showVersionLabels = isSameAssessment && hasVersion;

  return (
    <>
      <TabsMenu
        activeTab={tab}
        setActiveTab={(tab) => {
          setTab(tab);
          mpEvent(MPEvents.ButtonClick, {
            button: tab === 1 ? 'Tab:Assessment 1' : tab === 2 ? 'Tab:Assessment 2' : 'Tab:Comparison View',
          });
        }}
        tabs={[
          {
            label: 'Comparison view',
            value: 0,
          },
          {
            label: showVersionLabels ? 'Version 1' : 'Assessment 1',
            tooltip: getVersionTooltip(versionA),
            value: 1,
          },
          {
            label: showVersionLabels ? 'Version 2' : 'Assessment 2',
            tooltip: getVersionTooltip(versionB),
            value: 2,
          },
        ]}
      />
      <Spacer $px={30} />
      {tab === 0 && (
        <ComparisonView
          dataA={dataA}
          dataB={dataB}
          comparison={comparison}
          genPath={genPath}
          versionA={versionA}
          versionB={versionB}
        />
      )}
      {tab === 1 && <AssessmentView data={dataA} genPath={(el) => genPath(el, 'A')} />}
      {tab === 2 && <AssessmentView data={dataB} genPath={(el) => genPath(el, 'B')} />}
    </>
  );
};

export default ScenariosCompare;
